import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from "react";

/* ============================================================
   SVG LOADING + PARSING
   ============================================================ */

async function fetchText(url, signal) {
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`Failed to fetch SVG (${r.status})`);
  return await r.text();
}

function extractSvgParts(svgText) {
  // Remove hardcoded width/height so viewBox controls scaling
  const cleaned = svgText
    .replace(/width="[^"]*"/i, "")
    .replace(/height="[^"]*"/i, "");

  const vbMatch = cleaned.match(/viewBox="([^"]+)"/i);
  const viewBox = vbMatch ? vbMatch[1] : `0 0 322.22221 344.44445`;

  const openTagEnd = cleaned.indexOf(">");
  const closeTagStart = cleaned.lastIndexOf("</svg");

  const inner =
    openTagEnd >= 0 && closeTagStart > openTagEnd
      ? cleaned.slice(openTagEnd + 1, closeTagStart)
      : cleaned;

  const nums = viewBox.trim().split(/[\s,]+/).map(Number);
  const vbW = Number.isFinite(nums[2]) ? nums[2] : 322.22221;

  return { viewBox, inner, vbW };
}

/* ============================================================
   SVG UTILITIES
   ============================================================ */

function setDisplay(el, show) {
  if (!el) return;
  el.style.display = show ? "inline" : "none";
}

/**
 * Hide all jaw variants, then show only requested ids.
 */
function applyRender(svgRootEl, showIds) {
  if (!svgRootEl) return;

  const all = svgRootEl.querySelectorAll(
    'g[id^="upper_jaw_"], g[id^="lower_jaw_"]'
  );

  all.forEach((el) => setDisplay(el, false));

  (showIds || []).forEach((id) => {
    const el = svgRootEl.querySelector(`#${CSS.escape(id)}`);
    if (el) setDisplay(el, true);
  });
}

/**
 * Anchor reader (correct version).
 * Uses getCTM() so nested transforms are respected.
 */
function readAnchor(svgRootEl, id) {
  const el = svgRootEl?.querySelector(`#${CSS.escape(id)}`);
  if (!el) return null;

  const ctm = el.getCTM?.();
  if (ctm && Number.isFinite(ctm.e) && Number.isFinite(ctm.f)) {
    return { x: ctm.e, y: ctm.f };
  }
  return null;
}

/* ============================================================
   STAGE CONFIGURATION
   ============================================================ */

const ALL_STAGES = ["rest", "closure", "release"];
const ANIM_STAGES = ["closure", "release"]; // animation skips rest

function burstDuration(type) {
  if (type === "strong") return 160;
  if (type === "weak") return 110;
  return 140;
}

function burstStrength(type) {
  if (type === "strong") return 1.0;
  if (type === "weak") return 0.6;
  return 0;
}

function stageLabel(stage) {
  if (stage === "rest") return "Rest";
  if (stage === "closure") return "Closure";
  return "Release";
}

function stagePillClass(stage) {
  if (stage === "rest") return "bg-slate-100 text-slate-700 border-slate-200";
  if (stage === "closure") return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-sky-50 text-sky-800 border-sky-200";
}

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* ============================================================
   HOOK: prefers-reduced-motion
   ============================================================ */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;

    const update = () => setReduced(!!mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  return reduced;
}

/* ============================================================
   COMPONENT
   ============================================================ */

export default function ArticulationSideView({
  baseSvgSrc = `${process.env.REACT_APP_USER_API_URL}/uploads/diagrams/sounds.svg`,
  target,
  size = "normal", // "normal" | "large"
  fill = false, // ✅ if true: take parent height (prevents cropping in fixed wrappers)
  align = "center", // "center" | "top"  (✅ modal typically wants "top")
  onOpenModal = null,
  enableModalClick = true,
  showHint = true, // ✅ overlay hint (doesn't steal layout height)
}) {
  const svgRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  const [svgInner, setSvgInner] = useState(null);
  const [svgStatus, setSvgStatus] = useState("loading"); // loading | ready | error

  const [stage, setStage] = useState("closure");
  const [animating, setAnimating] = useState(true);

  const [mouthPos, setMouthPos] = useState(null);
  const [glottisPos, setGlottisPos] = useState(null);

  const [flowT, setFlowT] = useState(0);
  const [burstActive, setBurstActive] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);

  const [speedMs, setSpeedMs] = useState(650);

  const renderMap = target?.render || {};

  // Reduced motion: auto-pause
  useEffect(() => {
    if (reducedMotion) setAnimating(false);
  }, [reducedMotion]);

  /* ------------------------------------------------------------
     Load SVG (abortable)
  ------------------------------------------------------------ */
  useEffect(() => {
    const ac = new AbortController();
    let alive = true;

    setSvgStatus("loading");

    (async () => {
      try {
        const txt = await fetchText(baseSvgSrc, ac.signal);
        if (!alive) return;
        setSvgInner(extractSvgParts(txt));
        setSvgStatus("ready");
      } catch (e) {
        if (!alive) return;
        if (String(e?.name) === "AbortError") return;
        setSvgInner(null);
        setSvgStatus("error");
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [baseSvgSrc]);

  /* ------------------------------------------------------------
     Apply stage rendering + read anchors
  ------------------------------------------------------------ */
  useLayoutEffect(() => {
    const root = svgRef.current;
    if (!root) return;

    applyRender(root, renderMap?.[stage]?.show || []);
    setMouthPos(readAnchor(root, "anchor_mouth_exit"));
    setGlottisPos(readAnchor(root, "anchor_glottis"));
  }, [svgInner, stage, renderMap]);

  /* ------------------------------------------------------------
     Animation loop (closure ⇄ release)
  ------------------------------------------------------------ */
  useEffect(() => {
    if (!animating) return;
    if (reducedMotion) return;

    const id = setInterval(() => {
      setStage((s) => {
        const i = ANIM_STAGES.indexOf(s);
        return ANIM_STAGES[(i + 1) % ANIM_STAGES.length];
      });
    }, speedMs);

    return () => clearInterval(id);
  }, [animating, speedMs, reducedMotion]);

  const stepStage = useCallback(() => {
    setAnimating(false);
    setStage((s) => ALL_STAGES[(ALL_STAGES.indexOf(s) + 1) % ALL_STAGES.length]);
  }, []);

  /* ------------------------------------------------------------
     One-shot window (burst + voicing)
  ------------------------------------------------------------ */
  const burst = renderMap?.release?.burst || null;
  const wantsVoicingRelease = !!renderMap?.release?.voicing;
  const voicing = voiceActive && wantsVoicingRelease;

  useEffect(() => {
    let raf;

    const wantsBurstNow = stage === "release" && !!burst;
    const wantsVoicingNow = stage === "release" && wantsVoicingRelease;

    if (!wantsBurstNow && !wantsVoicingNow) {
      setBurstActive(false);
      setVoiceActive(false);
      setFlowT(0);
      return;
    }

    const D = burst ? burstDuration(burst) : 140;

    setBurstActive(wantsBurstNow);
    setVoiceActive(wantsVoicingNow);
    setFlowT(0);

    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / D);
      if (wantsBurstNow) setFlowT(t);

      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setBurstActive(false);
        setVoiceActive(false);
        setFlowT(0);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [stage, burst, wantsVoicingRelease]);

  /* ------------------------------------------------------------
     Scaling
  ------------------------------------------------------------ */
  const S = useMemo(() => {
    const w = svgInner?.vbW || 322.22221;
    return w / 322.22221;
  }, [svgInner?.vbW]);

  /* ------------------------------------------------------------
     Airflow geometry
  ------------------------------------------------------------ */
  const flow = useMemo(() => {
    const k = burstStrength(burst);
    const fade = 1 - flowT;

    const baseLen = 110 * S;
    const amp = 11 * S;
    const gap = 9 * S;
    const stroke = 4.5 * S;

    const opacity = Math.max(0, 0.95 * k * fade);
    const phase = flowT * Math.PI * 2;

    const lines = [0, 1, 2].map((i) => {
      const y = i * gap - gap;
      const len = baseLen * (0.82 + 0.18 * i);
      const a = amp * (0.85 + 0.25 * i);
      const p = phase + i * 1.1;

      return {
        d: `M 0 ${y}
            C ${-0.3 * len} ${y + Math.sin(p) * a},
              ${-0.6 * len} ${y + Math.sin(p + 1.4) * a},
              ${-len} ${y + Math.sin(p + 2.6) * a}`,
      };
    });

    return { lines, stroke, opacity };
  }, [S, burst, flowT]);

  /* ------------------------------------------------------------
     Voicing geometry
  ------------------------------------------------------------ */
  const voice = useMemo(
    () => ({
      shaftH: 38 * S,
      shaftW: 5.2 * S,
      headW: 15 * S,
      headH: 15 * S,
      wiggleDx: 1.6 * S,
      wiggleDy: 2.4 * S,
    }),
    [S]
  );

  /* ------------------------------------------------------------
     Modal open
  ------------------------------------------------------------ */
  const canOpenModal = enableModalClick && typeof onOpenModal === "function";
  const openModal = useCallback(() => {
    if (canOpenModal) onOpenModal();
  }, [canOpenModal, onOpenModal]);

  // ✅ This is the key: preserveAspectRatio decides vertical anchoring
  const preserve = align === "top" ? "xMidYMin meet" : "xMidYMid meet";

  // ✅ Height behavior:
  // - fill=true => h-full (parent controls height)
  // - otherwise, we give a sane default height
  const heightClass = fill
    ? "h-full"
    : size === "large"
    ? "h-[min(72vh,720px)]"
    : "h-[320px]";

  return (
    <div className="w-full">
      {/* Controls only in normal view (and only if not fill-large embed) */}
      {size !== "large" && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={cx(
                "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
                stagePillClass(stage)
              )}
              title="Current stage"
            >
              {stageLabel(stage)}
            </span>

            <span
              className={cx(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold",
                animating
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-slate-50 text-slate-700 border-slate-200"
              )}
              title={animating ? "Animation playing" : "Animation paused"}
            >
              <span
                className={cx(
                  "h-2 w-2 rounded-full",
                  animating ? "bg-emerald-500" : "bg-slate-400"
                )}
              />
              {animating ? "Playing" : "Paused"}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={stepStage}
              className="rounded-xl border bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 active:scale-[0.99]"
              title="Step through stages (rest → closure → release)"
            >
              Step
            </button>

            <button
              type="button"
              onClick={() => setAnimating((v) => !v)}
              className={cx(
                "rounded-xl border px-3 py-2 text-xs font-semibold active:scale-[0.99]",
                animating
                  ? "bg-sky-600 text-white border-sky-600 hover:bg-sky-700"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              )}
              title={
                reducedMotion
                  ? "Disabled by Reduced Motion setting"
                  : "Play/pause animation"
              }
              disabled={reducedMotion}
            >
              {animating ? "Pause" : "Play"}
            </button>

            <div className="hidden sm:flex items-center gap-2 rounded-xl border bg-white px-3 py-2">
              <span className="text-[11px] font-semibold text-slate-600">
                Speed
              </span>
              <input
                type="range"
                min={420}
                max={900}
                step={10}
                value={speedMs}
                onChange={(e) => setSpeedMs(parseInt(e.target.value, 10))}
                className="w-28"
                aria-label="Animation speed"
                disabled={reducedMotion}
              />
            </div>
          </div>
        </div>
      )}

      {/* STAGE CARD */}
      <div
        className={cx(
          "relative overflow-hidden rounded-3xl border",
          "bg-white shadow-[0_18px_50px_rgba(15,23,42,0.10)] ring-1 ring-black/5",
          heightClass,
          canOpenModal ? "cursor-pointer" : ""
        )}
        role={canOpenModal ? "button" : undefined}
        tabIndex={canOpenModal ? 0 : undefined}
        onClick={() => openModal()}
        onKeyDown={(e) => {
          if (!canOpenModal) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
        aria-label={canOpenModal ? "Open articulation diagram in modal" : undefined}
      >
        {/* Glow background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-28 -top-28 h-72 w-72 rounded-full bg-sky-200/60 blur-3xl" />
          <div className="absolute -right-28 -bottom-28 h-72 w-72 rounded-full bg-indigo-200/60 blur-3xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/0 to-slate-50/50" />
        </div>

        {/* Top HUD */}
        <div className="pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-2">
          <span
            className={cx(
              "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold backdrop-blur",
              stagePillClass(stage)
            )}
          >
            {stageLabel(stage)}
          </span>

          {(burstActive || voicing) && (
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50/80 px-3 py-1 text-[11px] font-semibold text-sky-800 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-sky-500" />
              {burstActive ? "Burst" : "Voicing"}
            </span>
          )}
        </div>

        {/* Loading / Error overlays */}
        {svgStatus !== "ready" && (
          <div className="absolute inset-0 z-20 flex items-center justify-center p-6">
            <div className="w-full max-w-sm rounded-2xl border bg-white/80 p-4 text-center shadow-sm backdrop-blur">
              {svgStatus === "loading" ? (
                <>
                  <div className="mx-auto h-10 w-10 animate-pulse rounded-full bg-slate-200" />
                  <div className="mt-3 text-sm font-semibold text-slate-700">
                    Loading diagram…
                  </div>
                  <div className="mt-2 space-y-2">
                    <div className="h-2 w-full animate-pulse rounded bg-slate-200" />
                    <div className="h-2 w-5/6 animate-pulse rounded bg-slate-200" />
                    <div className="h-2 w-3/4 animate-pulse rounded bg-slate-200" />
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-slate-800">
                    Diagram failed to load
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    Check the file URL or network. (baseSvgSrc)
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* SVG AREA (✅ h-full, no extra bottom text affecting layout) */}
        <div className="relative z-10 h-full w-full p-3">
          <div className="h-full w-full rounded-2xl border bg-white/70 backdrop-blur shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
            <svg
              ref={svgRef}
              viewBox={svgInner?.viewBox || `0 0 322.22221 344.44445`}
              preserveAspectRatio={preserve}
              style={{ width: "100%", height: "100%", display: "block" }}
            >
              <defs>
                <filter id="burstGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2.4" result="blur" />
                  <feColorMatrix
                    in="blur"
                    type="matrix"
                    values="
                      1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 0.7 0"
                    result="glow"
                  />
                  <feMerge>
                    <feMergeNode in="glow" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>

                <filter id="voiceGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.8" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>

                <linearGradient id="burstGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#60a5fa" />
                  <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
              </defs>

              {svgInner && (
                <g dangerouslySetInnerHTML={{ __html: svgInner.inner }} />
              )}

              {/* Burst */}
              {burstActive && mouthPos && (
                <g
                  transform={`translate(${mouthPos.x} ${mouthPos.y})`}
                  opacity={flow.opacity}
                  filter="url(#burstGlow)"
                >
                  {flow.lines.map((ln, i) => (
                    <path
                      key={i}
                      d={ln.d}
                      fill="none"
                      stroke="url(#burstGrad)"
                      strokeWidth={flow.stroke}
                      strokeLinecap="round"
                    />
                  ))}
                </g>
              )}

              {/* Voicing */}
              {voicing && glottisPos && (
                <g
                  transform={`translate(${glottisPos.x} ${glottisPos.y})`}
                  filter="url(#voiceGlow)"
                >
                  <g>
                    <animateTransform
                      attributeName="transform"
                      type="translate"
                      values={`0 0; ${voice.wiggleDx} ${-voice.wiggleDy}; 0 0`}
                      dur="0.18s"
                      repeatCount="indefinite"
                    />
                    <path
                      d={`M 0 0 L 0 ${-voice.shaftH}`}
                      stroke="#2563eb"
                      strokeWidth={voice.shaftW}
                      strokeLinecap="round"
                      fill="none"
                    />
                    <path
                      d={`M 0 ${-voice.shaftH - voice.headH}
                          L ${-voice.headW / 2} ${-voice.shaftH}
                          L ${voice.headW / 2} ${-voice.shaftH}
                          Z`}
                      fill="#2563eb"
                    />
                  </g>
                </g>
              )}
            </svg>
          </div>

          {/* ✅ Overlay hint (no layout impact) */}
          {canOpenModal && showHint && (
            <div className="pointer-events-none absolute bottom-4 left-0 right-0 text-center text-[11px] font-medium text-slate-500">
              Click to expand
            </div>
          )}
        </div>
      </div>
    </div>
  );
}